import { useState, useRef, type ReactNode } from 'react';
import { Table2, IdCard } from 'lucide-react';
import './SmartTable.css';

// A Markdown table with a per-table view toggle (Alex 2026-07-15): wide tables
// cram badly on mobile (words break mid-syllable). The "Karten"/ticker mode shows
// ONE row at a time as a vertical Header→Value card, with a slider to move between
// rows. Cell content stays as ReactNode so bold/code/links survive both modes.
export function SmartTable({ headers, rows }: { headers: ReactNode[]; rows: ReactNode[][] }) {
  const [mode, setMode] = useState<'table' | 'cards'>('table');
  const [idx, setIdx] = useState(0);
  const n = rows.length;

  // A single-column table is already vertical — no toggle needed.
  const multiCol = headers.length >= 2;
  const clampedIdx = Math.min(idx, Math.max(0, n - 1));

  const go = (next: number) => setIdx(Math.min(n - 1, Math.max(0, next)));

  // Swipe left/right to move between rows (Alex 2026-07-15). Horizontal-dominant
  // gesture only, so it never hijacks vertical scroll.
  const touch = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touch.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(clampedIdx + (dx < 0 ? 1 : -1));
  };

  return (
    <div className="smart-table">
      {multiCol && (
        <div className="st-toolbar">
          <button
            type="button"
            className={`st-mode${mode === 'table' ? ' active' : ''}`}
            onClick={() => setMode('table')}
            title="Als Tabelle anzeigen"
            aria-label="Tabellen-Ansicht"
          >
            <Table2 size={14} />
          </button>
          <button
            type="button"
            className={`st-mode${mode === 'cards' ? ' active' : ''}`}
            onClick={() => setMode('cards')}
            title="Zeile für Zeile als Karte (mit Slider)"
            aria-label="Karten-Ansicht"
          >
            <IdCard size={14} />
          </button>
        </div>
      )}

      {mode === 'table' || !multiCol ? (
        <div className="st-scroll">
          <table className="md-table">
            <thead>
              <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>{row.map((c, i) => <td key={i}>{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="st-cards" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <div className="st-card">
            {headers.map((h, i) => (
              <div className="st-pair" key={i}>
                <div className="st-key">{h}</div>
                <div className="st-val">{rows[clampedIdx]?.[i] ?? <span className="st-empty">—</span>}</div>
              </div>
            ))}
          </div>
          {/* Ticker dots — tap a dot to jump, or swipe the card left/right. */}
          <div className="st-dots" role="tablist">
            {rows.map((_, i) => (
              <button
                type="button"
                key={i}
                className={`st-dot${i === clampedIdx ? ' active' : ''}`}
                onClick={() => go(i)}
                aria-label={`Zeile ${i + 1} von ${n}`}
                aria-selected={i === clampedIdx}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
